/**
 * useFlag — runtime feature flag hook.
 *
 * Usage:
 *   const showBulkRedline = useFlag<boolean>('clm.bulk_redline')
 *   if (showBulkRedline) return <BulkRedlineButton />
 *
 * Returns `undefined` while loading (first render before the query
 * resolves) and the resolved value thereafter. The resolved value
 * respects priority: user > org > role > environment > flag.default.
 *
 * Auto-pulls the user / org / role context from `useAppStore`. The
 * environment value comes from import.meta.env.MODE (e.g. 'production',
 * 'development') so flags can be gated per-deploy without redeploy.
 *
 * Pairs with the audit chain — flag override flips should post a
 * `feature_flag.override.applied` entry via the admin UI (not from the
 * read path here).
 *
 * Schema + service live at:
 *   supabase/migrations/00291_feature_flags.sql
 *   web/src/services/featureFlagService.ts
 */

import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '@/store/appStore'
import { resolveFlag } from '@/services/featureFlagService'

interface UseFlagOptions {
  /** Override the current user/org/role context. Useful in tests or admin previews. */
  context?: {
    userId?: string | null
    orgId?: string | null
    role?: string | null
    environment?: string | null
  }
  /** How long the resolved value stays "fresh" in cache (ms). Default 60s. */
  staleTimeMs?: number
}

export function useFlag<T = unknown>(key: string, options?: UseFlagOptions): T | undefined {
  const currentUser = useAppStore((s) => s.currentUser)
  const ctx = options?.context ?? {
    userId: currentUser?.id ?? null,
    orgId: currentUser?.organizationId ?? null,
    role: currentUser?.role ?? null,
    environment: import.meta.env.MODE,
  }
  const { data } = useQuery({
    queryKey: ['feature-flag', key, ctx.userId, ctx.orgId, ctx.role, ctx.environment],
    queryFn: () => resolveFlag({ key, context: ctx }),
    staleTime: options?.staleTimeMs ?? 60_000,
    // Don't retry on failure — fall back to undefined so the consumer
    // can decide what to do (most callers will treat undefined as "off").
    retry: false,
  })
  return data as T | undefined
}

/**
 * Sync helper for places where async resolution isn't available
 * (e.g. early-render gating). Returns the flag's default_value if the
 * cache hasn't populated yet. Most callers should use `useFlag` instead.
 */
export function useFlagWithDefault<T>(key: string, fallback: T): T {
  const value = useFlag<T>(key)
  return value ?? fallback
}
